import React, { ReactNode, useState, useEffect, useContext, createContext } from 'react';
import { Box, IconButton, Avatar, Badge, Menu, MenuItem, ListItemIcon, Divider, Typography, useMediaQuery, useTheme, Fade } from '@mui/material';
import { GlassSpinner } from '../common/GlassLoader';
import apiClient from '../../services/api/config';
import { styled } from '@mui/material/styles';
import { PersonOutline, SettingsOutlined, Analytics, AdminPanelSettings, Logout, ArrowBack, NotificationsNone } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '../common/ErrorFallback';
import NavigationRail from './NavigationRail';
import BottomHubBar from './BottomHubBar';
import HubTabs from './HubTabs';
import EnhancedAiraAI from '../ai/EnhancedAiraAI';
import RecognitionQuestionsPopup from '../recognition/RecognitionQuestionsPopup';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { mobileHeaderFor } from '../../config/navHubs';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import logoMark from '../../assets/branding/logo-mark.svg';

const RAIL_WIDTH = 80;
const MOBILE_TOP_BAR = 56;

/**
 * Whether a layout is already above us in the tree.
 *
 * ProtectedRoute wraps every route in MainLayout, and 84 pages also import it
 * and wrap themselves — they predate that and were never unwound. Nested, you
 * get two navigation rails, two top bars and two assistants, which is what
 * the team board looked like. Rather than edit 84 files and rely on nobody
 * adding an 85th, the layout notices it is already inside one and gets out of
 * the way.
 */
const InsideLayout = createContext(false);

// `clip` rather than `hidden` on both boxes: `hidden` made each one a scroll
// container that never scrolls, so every position: sticky inside a page stuck
// to it instead of the screen. dvh tracks the mobile browser's toolbar.
const LayoutContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
  backgroundColor: theme.palette.background.default,
  overflowX: 'clip',
  maxWidth: '100vw',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  marginLeft: RAIL_WIDTH,
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
  padding: '24px',
  backgroundColor: theme.palette.background.default,
  maxWidth: '100%',
  overflowX: 'clip' as const,
  boxSizing: 'border-box' as const,
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    padding: '12px',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    // Clear the fixed top bar (56px + its own safe-area inset) + 12px gap,
    // and keep the last row above the fixed bottom hub bar (58px) + home indicator.
    paddingTop: `calc(${MOBILE_TOP_BAR + 12}px + env(safe-area-inset-top))`,
    paddingBottom: 'calc(70px + env(safe-area-inset-bottom))',
    maxWidth: '100vw',
  },
  // The shell already pads the page on phones; a <Container>'s own 16px
  // gutters on top of that left 304px of a 360px screen.
  [theme.breakpoints.down('sm')]: {
    '& .MuiContainer-root': { paddingLeft: 0, paddingRight: 0 },
  },
}));


interface MainLayoutProps {
  children: ReactNode;
}

function MainLayout({ children }: MainLayoutProps) {
  const nested = useContext(InsideLayout);
  // Hooks below this line must not be skipped, so the early return lives in a
  // wrapper rather than here.
  return nested ? <>{children}</> : <MainLayoutFrame>{children}</MainLayoutFrame>;
}

function MainLayoutFrame({ children }: MainLayoutProps) {
  const { user, isLoading, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const { branding } = useBranding();
  const { counts } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = !!user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';
  const header = mobileHeaderFor(location.pathname);

  const goAccount = (path: string) => { setAccountAnchor(null); navigate(path); };

  // Back within the app when there is app history to go back to; a deep link
  // opened cold (a notification, a shared link) goes to the route's parent.
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(header.parent, { replace: true });
  };

  // On phones the browser / OS status bar takes the top bar's colour, so the
  // installed app reads as one surface rather than a blue strip over a dark bar.
  useEffect(() => {
    if (!isMobile) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    meta.setAttribute('content', branding.sidebarColor || '#1A1D1F');
    return () => { meta.setAttribute('content', branding.primaryColor || '#2D5BE3'); };
  }, [isMobile, branding]);

  // Close the account menu on any route change (in-page navigations bypass onClick).
  useEffect(() => {
    setAccountAnchor(null);
  }, [location.pathname, location.hash]);

  // Lightweight page-view instrumentation — feeds the Insights engagement
  // analytics (DAU/WAU/MAU, top sections). Fire-and-forget; never blocks nav.
  useEffect(() => {
    apiClient.post('/insights/track', { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  const sidebarBg = branding.sidebarColor || '#1A1D1F';

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <GlassSpinner size={60} />
      </Box>
    );
  }

  return (
    <InsideLayout.Provider value>
    <LayoutContainer>
      {isMobile ? (
        <>
          <Box
            component="header"
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              // Grow the bar under the iOS status bar so its controls clear it
              // in installed/standalone mode (inset is 0 on non-notched devices).
              height: `calc(${MOBILE_TOP_BAR}px + env(safe-area-inset-top))`,
              backgroundColor: sidebarBg,
              color: branding.sidebarTextColor || '#FFFFFF',
              alignItems: 'center',
              gap: 0.5,
              padding: 'env(safe-area-inset-top) calc(8px + env(safe-area-inset-right)) 0 calc(8px + env(safe-area-inset-left))',
              zIndex: 1100,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {header.isChild ? (
              <IconButton onClick={goBack} aria-label="Back" sx={{ color: 'inherit' }}>
                <ArrowBack />
              </IconButton>
            ) : (
              <Box
                component="img"
                src={branding.logoUrl ? resolveAssetUrl(branding.logoUrl) : logoMark}
                alt={branding.companyName || 'MoreMe AI'}
                sx={{ width: 28, height: 28, mx: 1, objectFit: 'contain', ...(branding.logoUrl ? {} : { filter: 'brightness(10)' }) }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <Typography
              component="h1"
              noWrap
              sx={{ flex: 1, minWidth: 0, fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2, ml: 0.5 }}
            >
              {header.title || branding.companyName || 'MoreMe'}
            </Typography>
            <IconButton
              onClick={() => navigate('/notifications')}
              aria-label={counts.total > 0 ? `Notifications, ${counts.total} unread` : 'Notifications'}
              sx={{ color: 'inherit' }}
            >
              <Badge badgeContent={counts.total} max={99} color="error">
                <NotificationsNone />
              </Badge>
            </IconButton>
            <IconButton
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              aria-label="Account menu"
              sx={{ p: 0.75 }}
            >
              <Avatar
                src={user.avatar ? resolveAssetUrl(user.avatar) : undefined}
                sx={{ width: 32, height: 32, fontSize: 14, bgcolor: branding.primaryColor || '#2D5BE3' }}
              >
                {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: (t) => t.zIndex.tooltip + 10 }}
          >
            <MenuItem onClick={() => goAccount('/profile')}>
              <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => goAccount('/settings')}>
              <ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>
              Settings
            </MenuItem>
            {isAdmin && (
              <MenuItem onClick={() => goAccount('/insights')}>
                <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
                Insights
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={() => goAccount(isSuperSuperAdmin ? '/admin/super' : '/admin')}>
                <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
                Admin Hub
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={() => { setAccountAnchor(null); logout(); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
          <BottomHubBar />
        </>
      ) : (
        <NavigationRail />
      )}
      <MainContent>
        <HubTabs />
        {/* A page that throws shows its error here, inside the shell, so the
            bars stay usable — the app-level boundary used to replace
            everything, which in the installed app left no way to move on.
            Navigating elsewhere clears it. */}
        <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[location.pathname]}>
          <Fade in timeout={280} key={location.pathname}>
            <div>{children}</div>
          </Fade>
        </ErrorBoundary>
      </MainContent>
      <EnhancedAiraAI />
      <RecognitionQuestionsPopup />
    </LayoutContainer>
    </InsideLayout.Provider>
  );
}

export default MainLayout;
